import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { HotelReviewInput, VideoStoryInput } from '@pms/shared';
import {
  fetchHotelReviews,
  createHotelReview,
  updateHotelReview,
  deleteHotelReview,
  fetchVideoStories,
  createVideoStory,
  updateVideoStory,
  deleteVideoStory,
} from '@/services/api/storefront-content.api';

/**
 * React-Query hooks for curated storefront stories (TASK-62). Mirrors the
 * platform hook conventions (a `*Keys` factory + narrow invalidation). The
 * storefront reads these via a separate React-Query instance in another app and
 * refreshes on its own staleTime — no cross-app invalidation here.
 */

const SC_KEY = ['storefront-content'] as const;

export const storefrontKeys = {
  hotelReviews: [...SC_KEY, 'hotel-reviews'] as const,
  videoStories: [...SC_KEY, 'video-stories'] as const,
};

// ─── Hotel reviews ─────────────────────────────────────────────────────────────

export function useHotelReviews() {
  return useQuery({
    queryKey: storefrontKeys.hotelReviews,
    queryFn: fetchHotelReviews,
    staleTime: 15_000,
  });
}

function useInvalidateReviews() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: storefrontKeys.hotelReviews });
  };
}

export function useCreateHotelReview() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: (body: HotelReviewInput) => createHotelReview(body),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateHotelReview() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<HotelReviewInput> }) =>
      updateHotelReview(id, body),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteHotelReview() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: (id: string) => deleteHotelReview(id),
    onSuccess: () => invalidate(),
  });
}

// ─── Video stories ─────────────────────────────────────────────────────────────

export function useVideoStories() {
  return useQuery({
    queryKey: storefrontKeys.videoStories,
    queryFn: fetchVideoStories,
    staleTime: 15_000,
  });
}

function useInvalidateStories() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: storefrontKeys.videoStories });
  };
}

export function useCreateVideoStory() {
  const invalidate = useInvalidateStories();
  return useMutation({
    mutationFn: (body: VideoStoryInput) => createVideoStory(body),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateVideoStory() {
  const invalidate = useInvalidateStories();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<VideoStoryInput> }) =>
      updateVideoStory(id, body),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteVideoStory() {
  const invalidate = useInvalidateStories();
  return useMutation({
    mutationFn: (id: string) => deleteVideoStory(id),
    onSuccess: () => invalidate(),
  });
}
